import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Home, Plus, Save, Trash2, Pencil, X, CalendarClock, Banknote, Landmark, Building2, Sparkles, CheckCircle, ReceiptText } from 'lucide-react';

const typeOptions = [
  { value: 'mortgage', label: 'Hypotheek' },
  { value: 'rent', label: 'Huur' },
  { value: 'energy', label: 'Energie' },
  { value: 'internet', label: 'Internet' },
  { value: 'insurance', label: 'Verzekering' },
  { value: 'membership', label: 'Contributie' },
  { value: 'custom_fixed_cost', label: 'Zelf benoemen' },
];

const typeLabels = {
  mortgage: 'Hypotheek',
  rent: 'Huur',
  energy: 'Energie',
  internet: 'Internet',
  insurance: 'Verzekering',
  membership: 'Contributie',
  custom_fixed_cost: 'Eigen vaste last',
};

const mortgageValueReferenceOptions = [
  { value: 'woz', label: 'WOZ-waarde' },
  { value: 'market_value', label: 'Marktwaarde' },
];

const mortgageInterestTypeOptions = [
  { value: 'fixed_short', label: 'Kort rentevast' },
  { value: 'fixed_10y', label: '10 jaar rentevast' },
  { value: 'fixed_20y', label: '20 jaar rentevast' },
  { value: 'variable', label: 'Variabele rente' },
  { value: 'other', label: 'Anders' },
];

const mortgageRepaymentTypeOptions = [
  { value: 'annuity', label: 'Annuiteit' },
  { value: 'linear', label: 'Lineair' },
  { value: 'interest_only', label: 'Aflossingsvrij' },
  { value: 'other', label: 'Anders' },
];

const createEmptyLoanPart = () => ({
  id: `loan_part_${Math.random().toString(36).slice(2, 10)}`,
  name: '',
  repayment_type: 'annuity',
  outstanding_balance: '',
  interest_rate: '',
  original_balance: '',
  monthly_payment_override: '',
  monthly_principal_payment: '',
  snapshot_date: '',
  fixed_until_date: '',
});

const createEmptyForm = () => ({
  type: 'mortgage',
  provider: '',
  custom_type_label: '',
  custom_total_amount: '',
  custom_remaining_amount: '',
  monthly_cost: '',
  end_date: '',
  contract_type: 'annuity',
  market_average: '',
  is_auto_debit: true,
  next_payment_date: '',
  payment_day: '',
  payment_account_id: '',
  payment_account_name: '',
  payment_account_bank: '',
  mortgage_lender: '',
  mortgage_property_value: '',
  mortgage_value_reference: 'woz',
  mortgage_outstanding_balance: '',
  mortgage_interest_rate: '',
  mortgage_interest_type: 'fixed_10y',
  mortgage_snapshot_date: '',
  mortgage_term_months_total: '360',
  mortgage_term_months_remaining: '',
  mortgage_is_nhg: true,
  mortgage_loan_parts: [createEmptyLoanPart()],
  rent_is_indefinite: false,
});

const getMortgageInterestTypeLabel = (value) => {
  if (!value) return 'Onbekend';
  return mortgageInterestTypeOptions.find((option) => option.value === value)?.label || value;
};

export default function FixedCosts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(createEmptyForm);
  const [isMobileFormOpen, setIsMobileFormOpen] = useState(false);
  const [isDesktopFormOpen, setIsDesktopFormOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [contractsRes, accountsRes] = await Promise.all([
        apiClient.get('/contracts').catch(() => ({ data: [] })),
        apiClient.get('/accounts').catch(() => ({ data: [] })),
      ]);
      setContracts(contractsRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error('Error fetching fixed costs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(createEmptyForm());
    setIsMobileFormOpen(false);
    setIsDesktopFormOpen(false);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      type: item.type,
      provider: item.provider || '',
      custom_type_label: item.custom_type_label || '',
      custom_total_amount: String(item.custom_total_amount ?? ''),
      custom_remaining_amount: String(item.custom_remaining_amount ?? ''),
      monthly_cost: String(item.monthly_cost ?? ''),
      end_date: item.end_date ? new Date(item.end_date).toISOString().split('T')[0] : '',
      contract_type: item.contract_type || 'fixed',
      market_average: String(item.market_average ?? ''),
      is_auto_debit: Boolean(item.is_auto_debit),
      next_payment_date: item.next_payment_date ? new Date(item.next_payment_date).toISOString().split('T')[0] : '',
      payment_day: item.payment_day ? String(item.payment_day) : '',
      payment_account_id: item.payment_account_id || '',
      payment_account_name: item.payment_account_name || '',
      payment_account_bank: item.payment_account_bank || '',
      mortgage_lender: item.mortgage_lender || '',
      mortgage_property_value: String(item.mortgage_property_value ?? ''),
      mortgage_value_reference: item.mortgage_value_reference || 'woz',
      mortgage_outstanding_balance: String(item.mortgage_outstanding_balance ?? ''),
      mortgage_interest_rate: String(item.mortgage_interest_rate ?? ''),
      mortgage_interest_type: item.mortgage_interest_type || 'fixed_10y',
      mortgage_snapshot_date: item.mortgage_snapshot_date ? new Date(item.mortgage_snapshot_date).toISOString().split('T')[0] : '',
      mortgage_term_months_total: String(item.mortgage_term_months_total ?? '360'),
      mortgage_term_months_remaining: String(item.mortgage_term_months_remaining ?? ''),
      mortgage_is_nhg: Boolean(item.mortgage_is_nhg),
      rent_is_indefinite: Boolean(item.rent_is_indefinite),
      mortgage_loan_parts: Array.isArray(item.mortgage_loan_parts) && item.mortgage_loan_parts.length > 0
        ? item.mortgage_loan_parts.map((part) => ({
            id: part.id || `loan_part_${Math.random().toString(36).slice(2, 10)}`,
            name: part.name || '',
            repayment_type: part.repayment_type || 'annuity',
            outstanding_balance: String(part.outstanding_balance ?? ''),
            interest_rate: String(part.interest_rate ?? ''),
            original_balance: String(part.original_balance ?? ''),
            monthly_payment_override: String(part.monthly_payment_override ?? ''),
            monthly_principal_payment: String(part.monthly_principal_payment ?? ''),
            snapshot_date: part.snapshot_date ? new Date(part.snapshot_date).toISOString().split('T')[0] : '',
            fixed_until_date: part.fixed_until_date ? new Date(part.fixed_until_date).toISOString().split('T')[0] : '',
          }))
        : [createEmptyLoanPart()],
    });
    setIsMobileFormOpen(true);
    setIsDesktopFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveItem = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      monthly_cost: Number(form.monthly_cost),
      custom_type_label: form.custom_type_label?.trim() || null,
      custom_total_amount: form.custom_total_amount ? Number(form.custom_total_amount) : null,
      custom_remaining_amount: form.custom_remaining_amount ? Number(form.custom_remaining_amount) : null,
      market_average: Number(form.market_average || 0),
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      next_payment_date: form.next_payment_date ? new Date(form.next_payment_date).toISOString() : null,
      payment_day: form.payment_day ? Number(form.payment_day) : null,
      rent_is_indefinite: Boolean(form.rent_is_indefinite),
      mortgage_property_value: form.mortgage_property_value ? Number(form.mortgage_property_value) : null,
      mortgage_outstanding_balance: form.mortgage_outstanding_balance ? Number(form.mortgage_outstanding_balance) : null,
      mortgage_interest_rate: form.mortgage_interest_rate ? Number(form.mortgage_interest_rate) : null,
      mortgage_term_months_total: form.mortgage_term_months_total ? Number(form.mortgage_term_months_total) : null,
      mortgage_term_months_remaining: form.mortgage_term_months_remaining ? Number(form.mortgage_term_months_remaining) : null,
      mortgage_snapshot_date: form.mortgage_snapshot_date ? new Date(form.mortgage_snapshot_date).toISOString() : null,
      mortgage_loan_parts: (form.mortgage_loan_parts || [])
        .filter((part) => part.name.trim() || part.outstanding_balance || part.interest_rate)
        .map((part) => ({
          id: part.id,
          name: part.name.trim() || 'Leningdeel',
          repayment_type: part.repayment_type,
          outstanding_balance: Number(part.outstanding_balance || 0),
          interest_rate: Number(part.interest_rate || 0),
          original_balance: part.original_balance ? Number(part.original_balance) : null,
          monthly_payment_override: part.monthly_payment_override ? Number(part.monthly_payment_override) : null,
          monthly_principal_payment: part.monthly_principal_payment ? Number(part.monthly_principal_payment) : null,
          snapshot_date: part.snapshot_date ? new Date(part.snapshot_date).toISOString() : null,
          fixed_until_date: part.fixed_until_date ? new Date(part.fixed_until_date).toISOString() : null,
        })),
    };
    try {
      if (editingId) {
        await apiClient.put(`/contracts/${editingId}`, payload);
      } else {
        await apiClient.post('/contracts', payload);
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving fixed cost:', error);
      alert(error.response?.data?.detail || 'Opslaan is niet gelukt');
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Vaste last verwijderen?')) return;
    await apiClient.delete(`/contracts/${id}`);
    fetchData();
  };

  const totals = useMemo(() => {
    const totalMonthly = contracts.reduce((sum, c) => sum + Number(c.monthly_cost || 0), 0);
    const autoDebitCount = contracts.filter((c) => c.is_auto_debit).length;
    const next7Count = contracts.filter((c) => {
      if (!c.next_payment_date) return false;
      const days = Math.ceil((new Date(c.next_payment_date) - new Date()) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 7;
    }).length;
    return { totalMonthly, autoDebitCount, next7Count };
  }, [contracts]);

  const hasFixedCosts = contracts.length > 0;
  const mortgages = contracts.filter((c) => c.type === 'mortgage');
  const rents = contracts.filter((c) => c.type === 'rent');
  const customFixedCosts = contracts.filter((c) => c.type === 'custom_fixed_cost');
  const isMortgageForm = form.type === 'mortgage';
  const isCustomFixedCostForm = form.type === 'custom_fixed_cost';
  const isRentForm = form.type === 'rent';
  const isContractStyleForm = !isCustomFixedCostForm;
  const mortgagePreview = useMemo(() => {
    if (!isMortgageForm) {
      return null;
    }

    const totalMonths = Number(form.mortgage_term_months_total || 0);
    const remainingMonths = Number(form.mortgage_term_months_remaining || 0);
    const validParts = (form.mortgage_loan_parts || []).filter((part) => Number(part.outstanding_balance || 0) > 0);

    if (validParts.length === 0) {
      return null;
    }

    let monthlyInterest = 0;
    let monthlyPrincipal = 0;
    let monthlyPayment = 0;
    let usesManualData = false;

    validParts.forEach((part) => {
      const outstanding = Number(part.outstanding_balance || 0);
      const interestRate = Number(part.interest_rate || 0);
      const originalBalance = Number(part.original_balance || 0);
      const monthlyOverride = Number(part.monthly_payment_override || 0);
      const principalOverride = Number(part.monthly_principal_payment || 0);
      const interestPortion = outstanding * (interestRate / 100) / 12;

      let payment = 0;
      let principal = 0;

      if (monthlyOverride > 0) {
        payment = monthlyOverride;
        principal = Math.max(monthlyOverride - interestPortion, 0);
        if (principalOverride > 0) {
          principal = principalOverride;
        }
        usesManualData = true;
      } else if (part.repayment_type === 'interest_only') {
        payment = interestPortion;
        principal = 0;
      } else if (part.repayment_type === 'linear') {
        if (principalOverride > 0) {
          principal = principalOverride;
          usesManualData = true;
        } else if (originalBalance > 0 && totalMonths > 0) {
          principal = originalBalance / totalMonths;
        } else if (remainingMonths > 0) {
          principal = outstanding / remainingMonths;
        }
        payment = interestPortion + principal;
      } else if (remainingMonths > 0) {
        const monthlyRate = (interestRate / 100) / 12;
        if (monthlyRate > 0) {
          payment = outstanding * monthlyRate / (1 - (1 + monthlyRate) ** (-remainingMonths));
        } else {
          payment = outstanding / remainingMonths;
        }
        principal = Math.max(payment - interestPortion, 0);
      } else {
        payment = interestPortion;
        principal = 0;
      }

      monthlyInterest += interestPortion;
      monthlyPrincipal += principal;
      monthlyPayment += payment;
    });

    return {
      monthlyInterest,
      monthlyPrincipal,
      monthlyPayment,
      usesManualData,
    };
  }, [form.mortgage_loan_parts, form.mortgage_term_months_remaining, form.mortgage_term_months_total, isMortgageForm]);

  useEffect(() => {
    if (!isMortgageForm || !mortgagePreview) {
      return;
    }

    const computedMonthlyCost = mortgagePreview.monthlyPayment.toFixed(2);
    setForm((prev) => (
      prev.monthly_cost === computedMonthlyCost
        ? prev
        : { ...prev, monthly_cost: computedMonthlyCost }
    ));
  }, [isMortgageForm, mortgagePreview]);
  const scrollToForm = () => {
    setIsMobileFormOpen(true);
    setIsDesktopFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateLoanPart = (loanPartId, key, value) => {
    setForm((prev) => ({
      ...prev,
      mortgage_loan_parts: prev.mortgage_loan_parts.map((part) => (
        part.id === loanPartId ? { ...part, [key]: value } : part
      )),
    }));
  };

  const addLoanPart = () => {
    setForm((prev) => ({
      ...prev,
      mortgage_loan_parts: [...(prev.mortgage_loan_parts || []), createEmptyLoanPart()],
    }));
  };

  const removeLoanPart = (loanPartId) => {
    setForm((prev) => ({
      ...prev,
      mortgage_loan_parts: (prev.mortgage_loan_parts || []).filter((part) => part.id !== loanPartId),
    }));
  };

  const fixedCostForm = (
    <form onSubmit={saveItem} className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Type vaste last</p>
        <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
          <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            {typeOptions.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{isCustomFixedCostForm ? 'Organisatie of ontvanger' : 'Aanbieder of naam'}</p>
        <Input placeholder={isCustomFixedCostForm ? 'Bijv. Belastingdienst' : 'Bijv. Vattenfall'} value={form.provider} onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))} required />
      </div>
      {isCustomFixedCostForm && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Soort vaste last of schuld</p>
          <Input placeholder="Bijv. Belastingschuld" value={form.custom_type_label} onChange={(e) => setForm((p) => ({ ...p, custom_type_label: e.target.value }))} required />
        </div>
      )}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{isCustomFixedCostForm ? 'Maandbedrag of aflossing' : 'Bedrag per maand'}</p>
        <Input type="number" step="0.01" min="0" placeholder="0,00" value={form.monthly_cost} onChange={(e) => setForm((p) => ({ ...p, monthly_cost: e.target.value }))} required />
        {isMortgageForm && mortgagePreview && (
          <p className="text-xs text-muted-foreground">
            Berekend uit leningdelen: €{mortgagePreview.monthlyPayment.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}({mortgagePreview.usesManualData ? 'deels op basis van huidige deelbedragen' : 'geschat op basis van rente en looptijd'})
          </p>
        )}
      </div>
      {isContractStyleForm ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">{isRentForm ? 'Einddatum huurcontract' : 'Loopt af op'}</p>
          <Input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
            required={!isRentForm || !form.rent_is_indefinite}
            disabled={isRentForm && form.rent_is_indefinite}
          />
          {isRentForm && (
            <label className="flex items-center gap-2 text-sm pt-1">
              <input
                type="checkbox"
                checked={Boolean(form.rent_is_indefinite)}
                onChange={(e) => setForm((p) => ({
                  ...p,
                  rent_is_indefinite: e.target.checked,
                  end_date: e.target.checked ? '' : p.end_date,
                }))}
              />
              Huur is voor onbepaalde tijd
            </label>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Verwachte einddatum of laatste termijn</p>
          <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} />
        </div>
      )}
      {isContractStyleForm ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Soort contract</p>
          <Input placeholder={isMortgageForm ? 'Bijv. annuiteit' : (isRentForm ? 'Bijv. kale huur of inclusief servicekosten' : 'Bijv. vast of variabel')} value={form.contract_type} onChange={(e) => setForm((p) => ({ ...p, contract_type: e.target.value }))} required />
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Soort betaling</p>
          <Input placeholder="Bijv. maandelijkse aflossing" value={form.contract_type} onChange={(e) => setForm((p) => ({ ...p, contract_type: e.target.value }))} />
        </div>
      )}
      {isCustomFixedCostForm && (
        <>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Totaalbedrag schuld of regeling</p>
            <Input type="number" step="0.01" min="0" placeholder="0,00" value={form.custom_total_amount} onChange={(e) => setForm((p) => ({ ...p, custom_total_amount: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Nog openstaand</p>
            <Input type="number" step="0.01" min="0" placeholder="0,00" value={form.custom_remaining_amount} onChange={(e) => setForm((p) => ({ ...p, custom_remaining_amount: e.target.value }))} />
          </div>
        </>
      )}
      {!isMortgageForm && !isCustomFixedCostForm ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Vergelijkbedrag per maand</p>
          <Input type="number" step="0.01" min="0" placeholder="Wat betaal je gemiddeld elders?" value={form.market_average} onChange={(e) => setForm((p) => ({ ...p, market_average: e.target.value }))} />
        </div>
      ) : isMortgageForm ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 md:col-span-1">
          Bij hypotheek gebruiken we liever woningwaarde en leningdelen dan een vaag marktgemiddelde.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 md:col-span-1">
          Voor schulden rekenen we geen vergelijking of besparing uit. Hier gaat het om overzicht en voortgang.
        </div>
      )}
      <label className="flex items-center gap-2 text-sm rounded-lg border p-3">
        <input type="checkbox" checked={form.is_auto_debit} onChange={(e) => setForm((p) => ({ ...p, is_auto_debit: e.target.checked }))} />
        {isCustomFixedCostForm ? 'Gaat dit automatisch van je rekening af?' : 'Automatische incasso'}
      </label>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{isCustomFixedCostForm ? 'Eerstvolgende betaaldatum' : 'Eerstvolgende afschrijving'}</p>
        <Input type="date" value={form.next_payment_date} onChange={(e) => setForm((p) => ({ ...p, next_payment_date: e.target.value }))} />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Vaste betaaldag van de maand</p>
        <Input type="number" min="1" max="31" placeholder="Bijv. 27" value={form.payment_day} onChange={(e) => setForm((p) => ({ ...p, payment_day: e.target.value }))} />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Betaalrekening</p>
        <Select
        value={form.payment_account_id || 'none'}
        onValueChange={(value) => {
          if (value === 'none') return setForm((p) => ({ ...p, payment_account_id: '', payment_account_name: '', payment_account_bank: '' }));
          const acc = accounts.find((a) => a.id === value);
          setForm((p) => ({ ...p, payment_account_id: value, payment_account_name: acc?.account_name || '', payment_account_bank: acc?.bank_name || '' }));
        }}
      >
        <SelectTrigger><SelectValue placeholder="Rekening" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Geen rekening</SelectItem>
          {accounts.map((acc) => <SelectItem key={acc.id} value={acc.id}>{acc.bank_name} - {acc.account_name}</SelectItem>)}
        </SelectContent>
        </Select>
      </div>

      {isMortgageForm && (
        <>
          <div className="md:col-span-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="font-medium">Samenvatting van je hypotheek</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Vul hier de hoofdlijnen in. De leningdelen hieronder zijn de echte onderbouwing van openstaand bedrag, rente en maandlast.
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Hypotheekverstrekker</p>
            <Input placeholder="Bijv. Nationale Nederlanden" value={form.mortgage_lender} onChange={(e) => setForm((p) => ({ ...p, mortgage_lender: e.target.value }))} required />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Woningwaarde</p>
            <Input type="number" step="0.01" min="0" placeholder="Bijv. 325000" value={form.mortgage_property_value} onChange={(e) => setForm((p) => ({ ...p, mortgage_property_value: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Waardebron</p>
            <Select value={form.mortgage_value_reference} onValueChange={(value) => setForm((p) => ({ ...p, mortgage_value_reference: value }))}>
              <SelectTrigger><SelectValue placeholder="Kies WOZ of marktwaarde" /></SelectTrigger>
              <SelectContent>
                {mortgageValueReferenceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Openstaande hypotheek totaal</p>
            <Input type="number" step="0.01" min="0" placeholder="Som van alle leningdelen" value={form.mortgage_outstanding_balance} onChange={(e) => setForm((p) => ({ ...p, mortgage_outstanding_balance: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Gemiddelde rente (%)</p>
            <Input type="number" step="0.01" min="0" placeholder="Bijv. 1,84" value={form.mortgage_interest_rate} onChange={(e) => setForm((p) => ({ ...p, mortgage_interest_rate: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Rentevorm</p>
            <Select value={form.mortgage_interest_type} onValueChange={(value) => setForm((p) => ({ ...p, mortgage_interest_type: value }))}>
              <SelectTrigger><SelectValue placeholder="Kies rentevorm" /></SelectTrigger>
              <SelectContent>
                {mortgageInterestTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Stand per</p>
            <Input type="date" value={form.mortgage_snapshot_date} onChange={(e) => setForm((p) => ({ ...p, mortgage_snapshot_date: e.target.value }))} />
            <p className="text-xs text-muted-foreground">
              Vanaf deze peildatum rekent de app de restschuld en maandlast verder door.
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Totale looptijd (maanden)</p>
            <Input type="number" min="1" placeholder="Bijv. 360" value={form.mortgage_term_months_total} onChange={(e) => setForm((p) => ({ ...p, mortgage_term_months_total: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Resterende looptijd (maanden)</p>
            <Input type="number" min="1" placeholder="Bijv. 334" value={form.mortgage_term_months_remaining} onChange={(e) => setForm((p) => ({ ...p, mortgage_term_months_remaining: e.target.value }))} required />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-3">
            <input type="checkbox" checked={form.mortgage_is_nhg} onChange={(e) => setForm((p) => ({ ...p, mortgage_is_nhg: e.target.checked }))} />
            NHG van toepassing
          </label>
          <div className="md:col-span-3 rounded-xl border p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">Leningdelen</p>
                <p className="text-sm text-muted-foreground">
                  Voeg elk leningdeel apart toe. Denk aan lineair, annuiteit of aflossingsvrij met een eigen openstaand bedrag, rente en eventueel huidig maandbedrag.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={addLoanPart}>
                <Plus className="w-4 h-4 mr-2" />
                Leningdeel
              </Button>
            </div>
            <div className="space-y-3">
              {(form.mortgage_loan_parts || []).map((part, index) => (
                <div key={part.id} className="grid grid-cols-1 gap-3 rounded-lg border p-3 md:grid-cols-4">
                  <Input
                    placeholder={`Naam leningdeel ${index + 1}`}
                    value={part.name}
                    onChange={(e) => updateLoanPart(part.id, 'name', e.target.value)}
                  />
                  <Select
                    value={part.repayment_type}
                    onValueChange={(value) => updateLoanPart(part.id, 'repayment_type', value)}
                  >
                    <SelectTrigger><SelectValue placeholder="Type leningdeel" /></SelectTrigger>
                    <SelectContent>
                      {mortgageRepaymentTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Openstaand bedrag"
                    value={part.outstanding_balance}
                    onChange={(e) => updateLoanPart(part.id, 'outstanding_balance', e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Rente (%)"
                      value={part.interest_rate}
                      onChange={(e) => updateLoanPart(part.id, 'interest_rate', e.target.value)}
                    />
                    {(form.mortgage_loan_parts || []).length > 1 && (
                      <Button type="button" variant="ghost" onClick={() => removeLoanPart(part.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Oorspronkelijke hoofdsom"
                    value={part.original_balance}
                    onChange={(e) => updateLoanPart(part.id, 'original_balance', e.target.value)}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Huidig bruto bedrag p/m"
                    value={part.monthly_payment_override}
                    onChange={(e) => updateLoanPart(part.id, 'monthly_payment_override', e.target.value)}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Aflossing p/m"
                    value={part.monthly_principal_payment}
                    onChange={(e) => updateLoanPart(part.id, 'monthly_principal_payment', e.target.value)}
                  />
                  <Input
                    type="date"
                    placeholder="Stand per"
                    value={part.snapshot_date}
                    onChange={(e) => updateLoanPart(part.id, 'snapshot_date', e.target.value)}
                  />
                  <Input
                    type="date"
                    placeholder="Rente vast tot"
                    value={part.fixed_until_date}
                    onChange={(e) => updateLoanPart(part.id, 'fixed_until_date', e.target.value)}
                  />
                </div>
              ))}
            </div>
            {mortgagePreview && (
            <div className="rounded-lg border border-primary/15 bg-primary/5 p-3 text-sm">
                <p className="font-medium">Actuele maandlast op basis van je leningdelen</p>
                <p className="mt-1 font-mono text-lg font-semibold">
                  €{mortgagePreview.monthlyPayment.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="mt-1 text-muted-foreground">
                  Rente: €{mortgagePreview.monthlyInterest.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {' · '}
                  Aflossing: €{mortgagePreview.monthlyPrincipal.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      <div className="md:col-span-3 flex gap-2">
        <Button type="submit">{editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}{editingId ? 'Opslaan' : 'Toevoegen'}</Button>
        {editingId && <Button type="button" variant="outline" onClick={resetForm}><X className="w-4 h-4 mr-2" />Annuleren</Button>}
      </div>
    </form>
  );

  if (loading) return <div className="h-24 animate-pulse bg-muted rounded-xl" />;

  return (
    <div className="space-y-6">
      <AppPageIntro
        eyebrow="Planning en budget"
        title="Vaste Lasten"
        description="Je budgetlaag: wat structureel terugkomt, wanneer het afgaat en wat het per maand kost. Hier stuur je op maanddruk, rekening en betaaldag."
        badges={['Budgetlaag', 'Automatische incasso', 'Maandplanning']}
      />

      <Card className="shadow-card border-slate-200 bg-slate-50/70">
        <CardContent className="grid gap-4 p-5 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Planning</Badge>
              <Badge variant="outline">Maandbudget</Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold">Waar gebruik je Vaste lasten voor?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Hier beheer je wat maandelijks op je budget drukt: bedrag, betaaldag, rekening en verwachte afschrijving.
              Denk aan huur, hypotheek, energie, internet of een betalingsregeling.
            </p>
          </div>
          <div className="rounded-2xl border bg-background p-4">
            <p className="font-medium">Contractgegevens nodig?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Voor contractnummer, klantnummer, mobiel nummer, ingangsdatum en einddatum gebruik je liever de contractenmodule.
            </p>
            <Button className="mt-4 rounded-full" variant="outline" onClick={() => navigate('/contracts')}>
              Naar Contracten
            </Button>
          </div>
        </CardContent>
      </Card>

      {!hasFixedCosts && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Begin met je vaste lasten
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">1. Kies je type vaste last</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Begin bijvoorbeeld met hypotheek, huur, energie of internet.
              </p>
              <Button className="mt-4 w-full rounded-full" onClick={scrollToForm}>
                Naar formulier
              </Button>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">2. Koppel een betaalrekening</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Dat maakt je planning en betaaloverzicht duidelijker en completer.
              </p>
              <Button className="mt-4 w-full rounded-full" variant="outline" onClick={scrollToForm}>
                Nu invullen
              </Button>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">3. Voeg een betaaldatum toe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Zo zie je later sneller wat er de komende dagen wordt afgeschreven.
              </p>
              <Button className="mt-4 w-full rounded-full" variant="outline" onClick={scrollToForm}>
                Eerst toevoegen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Totaal per maand</p><p className="font-mono text-2xl font-bold">€{totals.totalMonthly.toFixed(2)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Automatische incasso’s</p><p className="font-mono text-2xl font-bold">{totals.autoDebitCount}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Betalingen binnen 7 dagen</p><p className="font-mono text-2xl font-bold">{totals.next7Count}</p></CardContent></Card>
      </div>

      <div className="md:hidden">
        <Card className="shadow-card">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <p className="font-medium">{editingId ? 'Vaste last wijzigen' : 'Vaste last toevoegen'}</p>
              <p className="text-sm text-muted-foreground">Open het formulier compact op mobiel.</p>
            </div>
            <Button className="rounded-full" onClick={() => setIsMobileFormOpen(true)}>
              {editingId ? 'Open formulier' : 'Toevoegen'}
            </Button>
          </CardContent>
        </Card>
        <Drawer open={isMobileFormOpen} onOpenChange={setIsMobileFormOpen}>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader>
              <DrawerTitle>{editingId ? 'Vaste last wijzigen' : 'Vaste last toevoegen'}</DrawerTitle>
              <DrawerDescription>
                Vul hier je vaste last in zonder dat het hele scherm al vol staat.
              </DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto px-4 pb-6">
              {fixedCostForm}
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      <div className="hidden md:block">
        <Collapsible open={isDesktopFormOpen} onOpenChange={setIsDesktopFormOpen}>
          <Card className="shadow-card">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle>{editingId ? 'Vaste last wijzigen' : 'Vaste last toevoegen'}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Open het formulier alleen wanneer je iets wilt toevoegen of aanpassen.
                </p>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant={isDesktopFormOpen ? 'outline' : 'default'} className="rounded-full">
                  {isDesktopFormOpen ? 'Formulier inklappen' : (editingId ? 'Wijzigen' : 'Toevoegen')}
                </Button>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                {fixedCostForm}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>

      <Card className="shadow-card">
        <CardHeader><CardTitle>Alle vaste lasten</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!hasFixedCosts ? (
            <div className="p-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <CheckCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-medium">Nog geen vaste lasten toegevoegd</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Voeg hierboven je eerste vaste last toe om maandlasten, betaalmomenten en woonkosten overzichtelijk te beheren.
              </p>
            </div>
          ) : (
            contracts.map((c) => (
              <div key={c.id} className="border rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium">{c.provider}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.type === 'custom_fixed_cost' ? (c.custom_type_label || 'Eigen vaste last') : (typeLabels[c.type] || c.type)}
                    {' · '}
                    {c.is_auto_debit ? 'Automatische incasso' : 'Handmatig'}
                    {' · '}
                    {c.payment_account_bank || '-'} {c.payment_account_name || ''}
                  </p>
                  {c.next_payment_date && (
                    <p className="text-xs text-muted-foreground">Volgende betaling: {new Date(c.next_payment_date).toLocaleDateString('nl-NL')}</p>
                  )}
                  {c.observed_monthly_average != null && (
                    <p className="text-xs text-muted-foreground">
                      Recente gemiddelde afschrijving: €{Number(c.observed_monthly_average || 0).toFixed(2)}
                      {c.observed_last_amount != null ? ` · laatste maand €${Number(c.observed_last_amount || 0).toFixed(2)}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-mono font-semibold">€{Number(c.monthly_cost).toFixed(2)}</p>
                  <div className="flex gap-1 mt-2 justify-end">
                    <Button size="sm" variant="secondary" onClick={() => startEdit(c)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => removeItem(c.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" />Huur overzicht</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {rents.length === 0 && <p className="text-sm text-muted-foreground">Nog geen huur ingevoerd.</p>}
          {rents.map((rent) => (
            <div key={rent.id} className="rounded-lg border p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold">{rent.provider}</p>
                <Badge variant="secondary">{rent.contract_type || 'maandelijks'}</Badge>
              </div>
              <p className="mt-3 font-mono text-lg font-semibold">€{Number(rent.monthly_cost || 0).toFixed(2)} per maand</p>
              <p className="text-xs text-muted-foreground mt-2">
                {rent.is_auto_debit ? 'Automatische incasso' : 'Handmatige betaling'}
                {rent.next_payment_date ? ` · volgende betaling ${new Date(rent.next_payment_date).toLocaleDateString('nl-NL')}` : ''}
              </p>
              {rent.rent_is_indefinite ? (
                <p className="text-xs text-muted-foreground mt-2">Looptijd: onbepaalde tijd</p>
              ) : rent.end_date ? (
                <p className="text-xs text-muted-foreground mt-2">Loopt af op {new Date(rent.end_date).toLocaleDateString('nl-NL')}</p>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><ReceiptText className="w-5 h-5" />Eigen vaste lasten</CardTitle></CardHeader>
        <CardContent className="space-y-3">
                  {customFixedCosts.length === 0 && <p className="text-sm text-muted-foreground">Nog geen eigen vaste lasten ingevoerd.</p>}
          {customFixedCosts.map((item) => (
            <div key={item.id} className="rounded-lg border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold">{item.custom_type_label || 'Eigen vaste last'}</p>
                  <p className="text-sm text-muted-foreground">{item.provider}</p>
                </div>
                <Badge variant="secondary">{item.contract_type || 'maandelijks'}</Badge>
              </div>
              <p className="mt-3 font-mono text-lg font-semibold">€{Number(item.monthly_cost || 0).toFixed(2)} per maand</p>
              {item.custom_total_amount ? (
                <div className="mt-3 rounded-lg bg-muted/40 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span>Totaalbedrag</span>
                    <span className="font-mono">€{Number(item.custom_total_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span>Nog openstaand</span>
                    <span className="font-mono">€{Number(item.custom_remaining_amount || 0).toFixed(2)}</span>
                  </div>
                  {item.custom_progress_percentage != null && (
                    <>
                      <div className="mt-3 h-2 rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full bg-primary"
                          style={{ width: `${Math.max(0, Math.min(Number(item.custom_progress_percentage || 0), 100))}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {Number(item.custom_progress_percentage || 0).toFixed(0)}% afgelost
                        {item.custom_paid_amount != null ? ` · al betaald €${Number(item.custom_paid_amount || 0).toFixed(2)}` : ''}
                      </p>
                    </>
                  )}
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground mt-2">
                {item.is_auto_debit ? 'Automatische incasso' : 'Handmatige betaling'}
                {item.next_payment_date ? ` · volgende betaling ${new Date(item.next_payment_date).toLocaleDateString('nl-NL')}` : ''}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><Home className="w-5 h-5" />Hypotheek overzicht</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {mortgages.length === 0 && <p className="text-sm text-muted-foreground">Nog geen hypotheek ingevoerd.</p>}
          {mortgages.map((m) => (
            <div key={m.id} className="rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{m.mortgage_lender || m.provider}</p>
                <Badge variant="secondary">{getMortgageInterestTypeLabel(m.mortgage_interest_type)}</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div className="flex items-center gap-2"><Landmark className="w-4 h-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Openstaand</p><p className="font-mono font-semibold">€{Number(m.mortgage_outstanding_balance || 0).toLocaleString('nl-NL')}</p></div></div>
                <div className="flex items-center gap-2"><Banknote className="w-4 h-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Rente</p><p className="font-mono font-semibold">{Number(m.mortgage_interest_rate || 0).toFixed(2)}%</p></div></div>
                <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Resterende looptijd</p><p className="font-mono font-semibold">{m.mortgage_term_months_remaining || '-'} maanden</p></div></div>
              </div>
              {m.mortgage_property_value ? (
                <p className="text-xs text-muted-foreground mt-3">
                  {m.mortgage_value_reference === 'market_value' ? 'Marktwaarde' : 'WOZ-waarde'}:
                  {' '}€{Number(m.mortgage_property_value || 0).toLocaleString('nl-NL')}
                </p>
              ) : null}
              {m.mortgage_snapshot_date ? (
                <p className="text-xs text-muted-foreground mt-1">
                  Stand per {new Date(m.mortgage_snapshot_date).toLocaleDateString('nl-NL')}
                </p>
              ) : null}
              {Array.isArray(m.mortgage_loan_parts) && m.mortgage_loan_parts.length > 0 && (
                <div className="mt-3 rounded-lg bg-muted/40 p-3">
                  <p className="text-xs font-medium text-muted-foreground">Leningdelen</p>
                  <div className="mt-2 space-y-2">
                    {m.mortgage_loan_parts.map((part) => {
                      const repaymentLabel = mortgageRepaymentTypeOptions.find((option) => option.value === part.repayment_type)?.label || part.repayment_type;
                      return (
                        <div key={part.id} className="flex flex-col gap-1 rounded-md border bg-background p-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="font-medium">{part.name}</p>
                            <p className="text-xs text-muted-foreground">{repaymentLabel}</p>
                          </div>
                          <div className="text-sm">
                            €{Number(part.outstanding_balance || 0).toLocaleString('nl-NL')} · {Number(part.interest_rate || 0).toFixed(2)}%
                            {(part.monthly_payment_override || part.monthly_principal_payment) ? (
                              <span className="block text-xs text-muted-foreground">
                                {part.monthly_payment_override ? `Huidig bruto p/m €${Number(part.monthly_payment_override || 0).toFixed(2)}` : ''}
                                {part.monthly_principal_payment ? ` · Aflossing €${Number(part.monthly_principal_payment || 0).toFixed(2)}` : ''}
                              </span>
                            ) : null}
                            {(part.snapshot_date || part.fixed_until_date) ? (
                              <span className="block text-xs text-muted-foreground">
                                {part.snapshot_date ? `Stand per ${new Date(part.snapshot_date).toLocaleDateString('nl-NL')}` : ''}
                                {part.fixed_until_date ? `${part.snapshot_date ? ' · ' : ''}Rente vast tot ${new Date(part.fixed_until_date).toLocaleDateString('nl-NL')}` : ''}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Maandlast nu: €{Number(m.monthly_cost || 0).toFixed(2)}
                {m.mortgage_monthly_interest != null ? ` · rente €${Number(m.mortgage_monthly_interest || 0).toFixed(2)}` : ''}
                {m.mortgage_monthly_principal != null ? ` · aflossing €${Number(m.mortgage_monthly_principal || 0).toFixed(2)}` : ''}
                {' · '}
                NHG: {m.mortgage_is_nhg ? 'Ja' : 'Nee'}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
